import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/app-context';
import { OrderStatus, VendorOrder } from '../types/order';
import { CustomButton } from '../elements/button';
import { isCollectionOrder } from '../utils/order-status-helpers';
import { CartContext } from '../context/cart-context';
import { useNavigate } from 'react-router-dom';
import { VIEW_CART_ROUTE } from '../constants/routes';

type Props = {
  vendorOrder: VendorOrder;
  vendorOrders: VendorOrder[];
  navigationImage: string;
  setSelectedOrderNavigationImage: React.Dispatch<React.SetStateAction<string>>;
  renderPosition: 'inline' | 'bottom';
};

export const VendorActionButton = ({
  vendorOrder,
  vendorOrders,
  navigationImage,
  setSelectedOrderNavigationImage,
  renderPosition,
}: Props) => {
  const { venueType, serviceMethod } = useContext(AppContext);
  const { addMultipleItemsToCart } = useContext(CartContext);
  const navigate = useNavigate();
  const { t } = useTranslation(['patron_portal', 'common']);
  const showDirections = isCollectionOrder(
    [vendorOrder],
    venueType?.value,
    serviceMethod?.value,
  );
  const isRenderAtPosition =
    (vendorOrders.length > 1 && renderPosition === 'inline') ||
    (vendorOrders.length === 1 && renderPosition === 'bottom');
  const itemsToCart = useMemo(
    () =>
      vendorOrder.items.map((item) => ({
        id: item._id,
        name: item.name,
        count: item.count,
        menuID: item.menuID,
        customisations: item.customisations?.map(
          (customisation) => customisation._id,
        ),
      })),
    [vendorOrder.items],
  );
  const handleCheckoutRetryPayment = () => {
    addMultipleItemsToCart(itemsToCart);
    navigate(VIEW_CART_ROUTE);
  };
  const renderShowDirectionsButton = () => (
    <CustomButton
      title={t('patron_portal.order.showDirections')}
      style={{ height: '48px', marginTop: '12px' }}
      onClick={() => setSelectedOrderNavigationImage(navigationImage)}
    />
  );
  const renderRetryPaymentButton = () => (
    <CustomButton
      title={t('patron_portal.order.btnCheckoutRetryPayment')}
      style={{ height: '48px', marginTop: '12px' }}
      onClick={handleCheckoutRetryPayment}
    />
  );
  if (!isRenderAtPosition) {
    return null;
  }
  if (showDirections) {
    return renderShowDirectionsButton();
  }
  if (vendorOrder.orderStatus === OrderStatus.PAYMENT_REJECTED) {
    return renderRetryPaymentButton();
  }
  return null;
};
